import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { askCards } from '../../services/readingService';
import CardResults from '../CardResults/CardResults';
import { SITE_LANGUAGE_CODES } from '../../data/siteLanguages';
import './ChatWidget.css';

const VISITOR_ID_KEY = 'chat_visitor_id';
const MAX_QUESTION_LENGTH = 500;
const LANGUAGE_CODES = new Set(SITE_LANGUAGE_CODES);

const WELCOME_MESSAGE = {
  id: 'welcome',
  type: 'system',
  text: 'Ask a tarot question. You’ll get 4 cards, an outlook, and an answer here.',
};

function languageFromPath(pathname) {
  const parts = (pathname || '').split('/').filter(Boolean);
  if (parts[0] === 'ai-psychic') {
    return LANGUAGE_CODES.has(parts[1]) ? parts[1] : 'en';
  }
  const first = parts[0] || '';
  return LANGUAGE_CODES.has(first) ? first : undefined;
}

const ChatWidget = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isAsking, setIsAsking] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const messagesEndRef = useRef(null);
  const askingRef = useRef(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  useEffect(() => {
    const openChat = () => setIsOpen(true);
    window.addEventListener('nowtarot:open-chat', openChat);
    return () => window.removeEventListener('nowtarot:open-chat', openChat);
  }, []);

  const connectPresence = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const storedVisitorId = localStorage.getItem(VISITOR_ID_KEY);
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl = `${protocol}//${window.location.host}/api/ws/chat`;
    const params = new URLSearchParams();

    if (user && user.id) {
      params.append('user_id', String(user.id));
      if (user.name) params.append('user_name', user.name);
      if (user.email) params.append('user_email', user.email);
      localStorage.removeItem(VISITOR_ID_KEY);
    } else if (storedVisitorId) {
      params.append('visitor_id', storedVisitorId);
    }

    if (params.toString()) {
      wsUrl += `?${params.toString()}`;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected' || data.type === 'presence' || data.type === 'pong') {
          if (typeof data.online_count === 'number') {
            setOnlineCount(data.online_count);
          }
          if (data.visitor_id && !String(data.visitor_id).startsWith('u')) {
            localStorage.setItem(VISITOR_ID_KEY, data.visitor_id);
          }
        }
      } catch (err) {
        // Ignore malformed presence payloads.
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (ws.pingTimer) {
        clearInterval(ws.pingTimer);
      }
      if (shouldReconnectRef.current && reconnectAttemptsRef.current < 40) {
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 15000);
        reconnectTimeoutRef.current = setTimeout(connectPresence, delay);
      }
    };

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      ws.pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);
    };
  }, [user]);

  const hideOnOwnerTools = location.pathname === '/llm' || location.pathname === '/translate' || location.pathname === '/chat-room/';

  useEffect(() => {
    if (hideOnOwnerTools || navigator.userAgent === 'ReactSnap') return undefined;
    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    connectPresence();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        if (wsRef.current.pingTimer) {
          clearInterval(wsRef.current.pingTimer);
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connectPresence, hideOnOwnerTools]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const updateReading = useCallback((id, patch) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, ...patch } : msg))
    );
  }, []);

  const handleSend = useCallback(async () => {
    const question = inputValue.trim().slice(0, MAX_QUESTION_LENGTH);
    if (!question || askingRef.current) return;

    askingRef.current = true;
    setIsAsking(true);
    setInputValue('');

    const userId = `user-${Date.now()}`;
    const readingId = `reading-${Date.now()}`;

    setMessages((prev) => [
      ...prev,
      { id: userId, type: 'user', text: question },
      {
        id: readingId,
        type: 'reading',
        cards: null,
        verdict: '',
        interpretation: '',
        isStreaming: true,
      },
    ]);

    let interpretation = '';

    await askCards(question, {
      language: languageFromPath(location.pathname),
      fromChat: true,
      onCards: (data) => {
        updateReading(readingId, {
          cards: data.cards,
          verdict: data.verdict,
          isStreaming: true,
        });
      },
      onToken: (token) => {
        interpretation += token;
        updateReading(readingId, {
          interpretation,
          isStreaming: true,
        });
      },
      onDone: () => {
        updateReading(readingId, {
          interpretation,
          isStreaming: false,
        });
      },
      onError: (msg) => {
        updateReading(readingId, {
          isStreaming: false,
          error: msg || 'Reading could not be completed. Please try again.',
        });
      },
    });

    askingRef.current = false;
    setIsAsking(false);
  }, [inputValue, location.pathname, updateReading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (hideOnOwnerTools) {
    return null;
  }

  return (
    <div className="chat-widget">
      <button
        className={`chat-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? 'Close chat' : 'Ask the tarot'}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="chat-bubble-label">{onlineCount} online</span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-info">
              <span className="chat-title">Ask the Tarot</span>
              <span className="chat-status connected">Private reading</span>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((msg) => {
              if (msg.type === 'system') {
                return (
                  <div key={msg.id} className="chat-message system">
                    <div className="message-content">{msg.text}</div>
                  </div>
                );
              }

              if (msg.type === 'user') {
                return (
                  <div key={msg.id} className="chat-message sent">
                    <div className="message-content">{msg.text}</div>
                  </div>
                );
              }

              return (
                <div key={msg.id} className="chat-message reading">
                  <div className="message-content reading-content">
                    {!msg.cards && !msg.error && (
                      <div className="reading-pending">Drawing your cards…</div>
                    )}
                    {msg.cards && (
                      <CardResults
                        cards={msg.cards}
                        verdict={msg.verdict}
                        interpretation={msg.interpretation}
                        isStreaming={msg.isStreaming}
                        compact
                      />
                    )}
                    {msg.error && (
                      <div className="reading-error">{msg.error}</div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <textarea
              className="chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isAsking ? 'Reading your cards…' : 'Ask a tarot question…'}
              disabled={isAsking}
              maxLength={MAX_QUESTION_LENGTH}
              rows={1}
            />
            <button
              className="chat-send"
              onClick={handleSend}
              disabled={isAsking || !inputValue.trim()}
              aria-label="Ask"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
